import { useEffect, useState, type ReactElement } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';

interface UrlEditModalProps {
  open: boolean;
  title: string;
  initialValue: string;
  isSaving: boolean;
  /** Permit saving an empty value (e.g. to remove the URL). */
  allowEmpty?: boolean;
  /** Helper text shown below the input while there is no validation error. */
  hint?: string;
  /** Returns an error message for a non-empty value, or null when valid. */
  validate?: (value: string) => string | null;
  onClose: () => void;
  onSave: (value: string) => void;
}

export function UrlEditModal({
  open,
  title,
  initialValue,
  isSaving,
  allowEmpty = false,
  hint,
  validate,
  onClose,
  onSave,
}: UrlEditModalProps): ReactElement {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmedValue = value.trim();
  const validationError = trimmedValue && validate ? validate(trimmedValue) : null;
  const saveDisabled = isSaving || (!trimmedValue && !allowEmpty) || Boolean(validationError);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:font-bold">
        {title}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          placeholder="URL"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={Boolean(validationError)}
          helperText={validationError ?? hint}
          className="vtwo:mt-2"
        />
      </DialogContent>
      <DialogActions className="vtwo:px-6 vtwo:pb-6 vtwo:gap-3">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
          className="vtwo:flex-1 vtwo:text-typography-primary vtwo:border-plus-border"
          sx={{ borderRadius: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(trimmedValue)}
          disabled={saveDisabled}
          className="vtwo:flex-1"
          sx={{
            borderRadius: '8px',
            backgroundColor: '#327D5D',
            '&:hover': { backgroundColor: '#28644A' },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
